import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../admin/dashboard/components/AdminLayout';
import { COLORS } from '../../../core/constants/colors';
import { Company } from '../../dashboard_user/types';
import {
  WebEnrichmentKillSwitch,
  WebEnrichmentStatus,
  WebEnrichmentSubmission,
  WebEnrichmentUsageEntry,
} from '../types';
import { webEnrichmentService } from '../services/web-enrichment.service';
import { api } from '../../../core/utils/apiClient';

const isValidUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return !!parsed.protocol && !!parsed.host;
  } catch {
    return false;
  }
};

export const AdminWebEnrichmentPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [consentAck, setConsentAck] = useState(false);

  const [, setSubmission] = useState<WebEnrichmentSubmission | null>(null);
  const [status, setStatus] = useState<WebEnrichmentStatus | null>(null);
  const [usage, setUsage] = useState<WebEnrichmentUsageEntry[]>([]);
  const [usageDays, setUsageDays] = useState(7);
  const [killSwitch, setKillSwitch] = useState<WebEnrichmentKillSwitch | null>(null);

  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [isUpdatingKillSwitch, setIsUpdatingKillSwitch] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
    loadKillSwitch();
    loadUsage(usageDays);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadStatus(selectedCompanyId);
    } else {
      setStatus(null);
      setSubmission(null);
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        const list: Company[] = data.companies || [];
        setCompanies(list);
        if (list.length > 0) {
          setSelectedCompanyId(list[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load companies', err);
      setError('Unable to load companies. Please refresh and try again.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadStatus = async (companyId: number) => {
    setIsFetchingStatus(true);
    setError(null);
    try {
      const result = await webEnrichmentService.getStatus(companyId);
      setStatus(result);
      if (result.website_url) {
        setWebsiteUrl(result.website_url);
      }
    } catch (err: any) {
      // No status yet is fine
      setStatus(null);
    } finally {
      setIsFetchingStatus(false);
    }
  };

  const loadUsage = async (days: number) => {
    setIsLoadingUsage(true);
    try {
      const result = await webEnrichmentService.getUsage(days);
      setUsage(result);
    } catch (err: any) {
      console.error('Failed to load usage:', err);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const loadKillSwitch = async () => {
    try {
      const result = await webEnrichmentService.getKillSwitch();
      setKillSwitch(result);
    } catch (err: any) {
      console.error('Failed to load kill switch:', err);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCompanyId) {
      setError('Select a company first.');
      return;
    }

    const targetUrl = websiteUrl.trim();
    if (!targetUrl) {
      setError('Please provide a website URL.');
      return;
    }

    if (!isValidUrl(targetUrl)) {
      setError('Enter a valid URL (including http:// or https://).');
      return;
    }

    if (!consentAck) {
      setError('You must acknowledge consent before submitting.');
      return;
    }

    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    try {
      const result = await webEnrichmentService.submitWebsite(
        selectedCompanyId,
        targetUrl,
        true
      );
      setSubmission(result);
      setMessage('Enrichment request submitted successfully.');
      await loadStatus(selectedCompanyId);
      setConsentAck(false);
    } catch (err: any) {
      setError(err.message || 'Failed to submit enrichment request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    if (!selectedCompanyId) return;
    setIsRetrying(true);
    setError(null);
    setMessage(null);
    try {
      await webEnrichmentService.retry(selectedCompanyId);
      setMessage('Retry requested successfully.');
      await loadStatus(selectedCompanyId);
    } catch (err: any) {
      setError(err.message || 'Unable to retry this request.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleToggleKillSwitch = async () => {
    if (!killSwitch) return;
    setIsUpdatingKillSwitch(true);
    setError(null);
    try {
      const updated = await webEnrichmentService.toggleKillSwitch(!killSwitch.is_enabled);
      setKillSwitch(updated);
      setMessage(`Kill switch ${updated.is_enabled ? 'enabled' : 'disabled'} successfully.`);
    } catch (err: any) {
      setError(err.message || 'Failed to update kill switch.');
    } finally {
      setIsUpdatingKillSwitch(false);
    }
  };

  const handleRefresh = () => {
    if (selectedCompanyId) {
      loadStatus(selectedCompanyId);
      setMessage('Status refreshed.');
    }
  };

  const handleRefreshUsage = () => {
    loadUsage(usageDays);
    setMessage('Usage data refreshed.');
  };

  const getStatusColor = (statusValue: string) => {
    switch (statusValue?.toLowerCase()) {
      case 'completed':
        return { bg: '#D1FAE5', text: '#059669' };
      case 'processing':
        return { bg: '#E3F2FD', text: '#1565C0' };
      case 'failed':
        return { bg: '#FFEBEE', text: '#C62828' };
      case 'pending':
        return { bg: '#FEF3C7', text: '#D97706' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  if (loadingCompanies) {
    return (
      <AdminLayout pageTitle="Web Enrichment" pageDescription="Manage website enrichment and monitor system usage">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statusColors = status ? getStatusColor(status.status) : null;

  return (
    <AdminLayout pageTitle="Web Enrichment" pageDescription="Manage website enrichment and monitor system usage">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8 space-y-6">
          {/* Messages */}
          {message && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">{message}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Kill Switch Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1F2937' }}>
                  Emergency Kill Switch
                </h3>
                <p className="text-sm text-gray-600">
                  Disable all enrichment requests system-wide
                </p>
              </div>
              <button
                onClick={handleToggleKillSwitch}
                disabled={isUpdatingKillSwitch || !killSwitch}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all disabled:opacity-50 ${
                  killSwitch?.is_enabled
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isUpdatingKillSwitch ? 'Updating...' : killSwitch?.is_enabled ? 'Disable System' : 'Enable System'}
              </button>
            </div>
            {killSwitch && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Current Status:</span>
                  <span className={`font-semibold ${killSwitch.is_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {killSwitch.is_enabled ? '✓ System Active' : '✗ System Disabled'}
                  </span>
                </div>
                {killSwitch.updated_at && (
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="text-gray-700">{new Date(killSwitch.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Enrichment Form */}
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#1F2937' }}>
                  Submit Enrichment Request
                </h3>

                <div className="space-y-4">
                  {/* Company Selector */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Select Company
                    </label>
                    <select
                      value={selectedCompanyId ?? ''}
                      onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={companies.length === 0}
                    >
                      {companies.length === 0 && <option value="">No companies available</option>}
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Website URL */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Website URL
                    </label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Consent */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="admin-consent"
                        checked={consentAck}
                        onChange={(e) => setConsentAck(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-gray-300"
                      />
                      <label htmlFor="admin-consent" className="text-sm text-gray-700 cursor-pointer">
                        I acknowledge consent to extract publicly available information from the provided website.
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !consentAck || !websiteUrl.trim()}
                    className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: COLORS.admin.primary }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit for Enrichment'}
                  </button>
                </div>
              </div>

              {/* Usage Statistics */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                    Usage Statistics
                  </h3>
                  <div className="flex items-center gap-3">
                    <select
                      value={usageDays}
                      onChange={(e) => {
                        const days = Number(e.target.value);
                        setUsageDays(days);
                        loadUsage(days);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={14}>Last 14 days</option>
                      <option value={30}>Last 30 days</option>
                    </select>
                    <button
                      onClick={handleRefreshUsage}
                      disabled={isLoadingUsage}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                      title="Refresh usage data"
                    >
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isLoadingUsage ? (
                  <div className="text-center py-8">
                    <div
                      className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                      style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                    />
                  </div>
                ) : usage.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Total</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Success</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Failed</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Quota</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {usage.map((entry, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{entry.date}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{entry.total_requests}</td>
                            <td className="px-4 py-3 text-right text-green-600">{entry.successful_requests}</td>
                            <td className="px-4 py-3 text-right text-red-600">{entry.failed_requests}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{entry.quota_remaining}/{entry.quota_limit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No usage data available for the selected period
                  </div>
                )}
              </div>
            </div>

            {/* Status Sidebar */}
            <div className="xl:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                    Enrichment Status
                  </h3>
                  <button
                    onClick={handleRefresh}
                    disabled={isFetchingStatus}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    title="Refresh status"
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>

                {isFetchingStatus && (
                  <div className="text-center py-8">
                    <div
                      className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                      style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                    />
                  </div>
                )}

                {!isFetchingStatus && status && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Status</p>
                      {statusColors && (
                        <span
                          className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                          style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                        >
                          {status.status}
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-2">Progress</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              width: `${status.completion_percentage ?? 0}%`,
                              backgroundColor: COLORS.admin.primary,
                            }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">
                          {status.completion_percentage ?? 0}%
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 mb-1">Fields Extracted</p>
                      <p className="text-lg font-bold text-gray-700">
                        {status.fields_extracted ?? 0} / {status.total_possible_fields ?? 20}
                      </p>
                    </div>

                    {status.extraction_started_at && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Started</p>
                        <p className="text-sm text-gray-700">
                          {new Date(status.extraction_started_at).toLocaleString()}
                        </p>
                      </div>
                    )}

                    {status.extraction_completed_at && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Completed</p>
                        <p className="text-sm text-gray-700">
                          {new Date(status.extraction_completed_at).toLocaleString()}
                        </p>
                      </div>
                    )}

                    {status.error_message && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-800">{status.error_message}</p>
                      </div>
                    )}

                    {status.can_retry && (
                      <button
                        onClick={handleRetry}
                        disabled={isRetrying}
                        className="w-full py-2.5 px-4 rounded-lg font-medium text-white transition-all disabled:opacity-50"
                        style={{ backgroundColor: COLORS.admin.primary }}
                      >
                        {isRetrying ? 'Retrying...' : 'Retry Enrichment'}
                      </button>
                    )}
                  </div>
                )}

                {!isFetchingStatus && !status && (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-gray-500">
                      No enrichment data yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
